import { bool } from "prop-types";
import { createContext, useContext, useMemo } from "react";

import styled from "styled-components/macro";
import { fixToCorner } from "theme";
import { colorVariants } from "theme/constants";

import FnrContainedIcon from "UI/FnrContainedIcon";
import CircleCheck from "UI/FnrEnrichedCheckbox/CircleCheck";
import FnrLoadingBricks from "UI/FnrLoadingBricks2";
import FnrSlides from "UI/FnrSlides";

const FnrStatusIconContext = createContext();

const size = "28px";

// Preserve the real-estate whether or not anything is displaying.
const SDiv = styled("div")`
  width: ${size};
  height: ${size};
  ${fixToCorner}
`;

const SizeMatcher = styled("div")`
  height: ${size};
  width: ${size};
`;

const componentKeys = {
  none: "none",
  FnrLoadingBricks: "FnrLoadingBricks",
  CircleCheck: "CircleCheck",
  error: "error",
};

const components = [
  {
    key: componentKeys.none,
    value: () => <SizeMatcher />,
  },
  { key: componentKeys.CircleCheck, value: () => <CircleCheck /> },
  {
    key: componentKeys.FnrLoadingBricks,
    value: () => (
      <SizeMatcher>
        <FnrLoadingBricks alignTop loading size={28} color="fnDarkBlue" />
      </SizeMatcher>
    ),
  },
  {
    key: componentKeys.error,
    value: () => (
      <FnrContainedIcon
        colorVariant={colorVariants.error}
        icon="times-circle fas"
        size="xl"
        data-cy="FnrStatusIcon_Error"
        center={false}
      />
    ),
  },
];

const Content = () => {
  const { isLoading, isSaved, hasError } = useContext(FnrStatusIconContext);
  const activeKey = useMemo(() => {
    if (isLoading) return componentKeys.FnrLoadingBricks;
    if (hasError) return componentKeys.error;
    if (isSaved) return componentKeys.CircleCheck;
    return componentKeys.none;
  }, [isLoading, isSaved, hasError]);

  return (
    <FnrSlides components={components} activeKey={activeKey} variant="subtle" />
  );
};

const FnrStatusIcon = ({ isLoading, isSaved, hasError, fixToLowerRight }) => (
  <FnrStatusIconContext.Provider value={{ isLoading, isSaved, hasError }}>
    <SDiv $noFixToCorner={!fixToLowerRight}>
      <Content />
    </SDiv>
  </FnrStatusIconContext.Provider>
);

FnrStatusIcon.propTypes = {
  isLoading: bool,
  isSaved: bool,
  fixToLowerRight: bool,
  hasError: bool,
};

export default FnrStatusIcon;
